import { useContext } from 'react'

import { AvatarUpload } from '~/components/AvatarUpload'
import { ShopContext } from '~/contexts'
import { makeShopOwnPath } from '~/helpers/routes'
import { useOptionalSeller } from '~/hooks/data/use-seller'
import { useShop } from '~/hooks/data/use-shop'
import { useOnboardingShake } from '~/hooks/use-onboarding-shake'
import { Avatar } from '~/ui'
import { cn } from '~/utils/classnames'
import { getImageKitUrl } from '~/utils/image'

import { ShopDescription } from './ShopDescription'
import { ShopName } from './ShopName'
import { ShopPromotions } from './ShopPromotions'
import { ShopShippingAndReturns } from './ShopShippingAndReturns'
import { ShopSlider, type ShopSliderProps } from './ShopSlider'

interface ShopHeroProps {
  slider: ShopSliderProps | null
}

export const ShopHero = ({ slider }: ShopHeroProps) => {
  const [{ themeColor }] = useContext(ShopContext)
  const shop = useShop()
  const seller = useOptionalSeller()

  const avatarRef = useOnboardingShake<HTMLDivElement | null>('avatar')
  const nameRef = useOnboardingShake<HTMLDivElement | null>('avatar', {
    shouldScroll: false,
  })

  return (
    <section
      className={cn(
        'relative w-full',
        'grid grid-cols-1 grid-rows-[auto_auto] gap-2 @sm:gap-4',
        '[&>*:last-child]:row-start-1 @md:[&>*:last-child]:col-start-2',
        '@md:flex @md:aspect-[2.4] @md:max-h-[85svh] @xl:gap-2.5'
      )}
    >
      <div
        className={cn(
          'bg-accent-element/12 flex grow flex-col items-start gap-4 rounded-md p-2 @sm:p-4',
          '@lg:p-6',
          themeColor
            ? 'bg-accent-element/12 light:bg-accent-element/8'
            : 'bg-foreground/8'
        )}
      >
        <div className={cn('flex', 'text-foreground z-10')}>
          <Avatar ref={avatarRef} className="size-14 @md:size-16">
            <Avatar.Fallback />
            {shop.avatarSrc && (
              <Avatar.Image
                src={getImageKitUrl(shop.avatarSrc || '', {
                  width: 100,
                  height: 100,
                  format: 'webp',
                })}
              />
            )}
            {seller && (
              <AvatarUpload
                avatarSrc={shop.avatarSrc}
                uploadAction={makeShopOwnPath(shop, '/settings?index')}
              />
            )}
          </Avatar>
        </div>
        <div className="w-full grow" ref={nameRef}>
          <ShopName />
          <ShopDescription />
        </div>
        <div className="flex flex-col items-start gap-2">
          <ShopShippingAndReturns />
          <ShopPromotions />
        </div>
      </div>

      {slider && (
        <ShopSlider
          {...slider}
          className={cn(
            '-ml-2 w-[calc(100%+1rem)] overflow-hidden',
            '@sm:ml-0 @sm:w-auto',
            '@md:h-full @md:max-h-[85svh]'
          )}
        />
      )}
    </section>
  )
}
